import { Controller } from "@hotwired/stimulus"

// Hide/show the impersonation bar (see shared/_impersonation_banner).
//
// The state is a root-element class rather than one on the bar itself, so the
// pre-paint script in the layout head can apply it before anything renders —
// a bar that paints orange for one frame on every navigation is no use to
// someone recording their screen. That script and this controller must agree
// on both the storage key and the class name.
//
// Stored as the current impersonation's token, not a boolean: "hidden" is a
// decision about THIS impersonation. A bare true would carry into the next
// person you signed in as, and you would never see the bar that tells you so —
// and a user id would do the same thing the second time you signed in as the
// same person. The token is minted per impersonation and dropped when it ends.
const KEY = "emailpa-impersonation-hidden-for"
const ROOT_CLASS = "impersonation-hidden"

export default class extends Controller {
    static values = { token: String }

    connect() {
        // The head script covers a full page load; this covers a Turbo visit
        // that swapped in a banner for a DIFFERENT impersonation, where the
        // root class would otherwise still be set from the previous one.
        this._apply(this.tokenValue.length > 0 && this._storedToken() === this.tokenValue)
    }

    hide() {
        this._store(this.tokenValue)
        this._apply(true)
    }

    show() {
        this._store(null)
        this._apply(false)
    }

    _apply(hidden) {
        document.documentElement.classList.toggle(ROOT_CLASS, hidden)
    }

    _storedToken() {
        try {
            return window.localStorage.getItem(KEY)
        } catch (e) {
            // Private mode, blocked site data: the bar simply stays visible,
            // which is the safe direction to fail in.
            return null
        }
    }

    _store(value) {
        try {
            if (value) window.localStorage.setItem(KEY, value)
            else window.localStorage.removeItem(KEY)
        } catch (e) {
            // Nothing to do — _apply still runs, the choice just won't survive
            // the next navigation.
        }
    }
}
